import type {} from '@mui/x-date-pickers-pro/themeAugmentation'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'
import { createTheme, Theme, ThemeOptions, PaletteColor, PaletteColorOptions } from '@mui/material/styles'
import {
  graphitePalette,
  tealPalette,
  bluePalette,
  greenPalette,
  // yellowPalette,
  orangePalette,
  redPalette,
  // magentaPalette,
  graphite,
  lightGraphite,
  darkGraphite,
  blue,
  lightBlue,
  darkBlue,
  green,
  lightGreen,
  darkGreen,
  yellow,
  lightYellow,
  darkYellow,
  magenta,
  lightMagenta,
  darkMagenta,
} from './colors'

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    xxl: true
  }

  interface Theme {
    sizes: {
      cardWidth: number
      drawerWidth: number
      pageWidth: number
      pageMargins: number | string
    }
  }

  interface ThemeOptions {
    sizes?: {
      cardWidth: number
      drawerWidth?: number
      pageWidth?: number
      pageMargins?: number | string
    }
  }

  interface Palette {
    kitchen: PaletteColor
    campus: PaletteColor
    kiosk: PaletteColor
    luxo: PaletteColor
    market: PaletteColor
    tools: PaletteColor
  }

  interface PaletteOptions {
    kitchen?: PaletteColorOptions
    campus?: PaletteColorOptions
    kiosk?: PaletteColorOptions
    luxo?: PaletteColorOptions
    market?: PaletteColorOptions
    tools?: PaletteColorOptions
  }
}

const fontFamily = 'Outfit, Helvetica, Arial, sans-serif'

export const options: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1600,
      xxl: 1920,
    }
  },

  typography: {
    fontFamily: fontFamily,
    allVariants: {
      fontFamily: fontFamily,
    },
  },

  palette: {
    // default theme palettes
    primary: graphitePalette,
    secondary: bluePalette,
    info: tealPalette,
    error: redPalette,
    warning: orangePalette,
    success: greenPalette,
    // custom theme palettes
    campus: {
      light: lightMagenta[50],
      main: magenta[700],
      dark: darkMagenta[800],
    },
    kitchen: {
      light: lightGreen[100],
      main: green[700],
      dark: darkGreen[900],
    },
    kiosk: {
      light: lightYellow[100],
      main: yellow[600],
      dark: darkYellow[800],
    },
    market: {
      light: lightBlue[50],
      main: blue[400],
      dark: darkBlue[600],
    },
    luxo: {
      light: lightGraphite[200],
      main: graphite[800],
      dark: darkGraphite[900],
    },
    tools: {
      light: lightGraphite[200],
      main: graphite[800],
      dark: darkGraphite[900],
    },
  },

  shape: {
    borderRadius: 10,
  },

  sizes: {
    cardWidth: 32,
    drawerWidth: 48,
    pageWidth: 108,
    pageMargins: 'auto',
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        root: `
          html, body {
            font-family: ${fontFamily};
          }
        `
      }
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        }
      }
    },

    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          color: theme.palette.common.black,
          boxShadow: 'none',
          borderBottomStyle: 'solid',
          borderBottomWidth: 1,
          borderBottomColor: theme.palette.divider,
          '@media print': {
            display: 'none',
          },
        })
      }
    },

    MuiAlert: {
    // EXAMPLE: Defining the variant styles to go along with the EXAMPLE variant="whatever" defined in /src/components/Alert/Alert.tsx
    //   variants: [
    //     {
    //       props: { variant: 'whatever' },
    //       style: ({ theme }) => ({
    //         SOME STYLES HERE...
    //       }),
    //     },
    //   ],
    },

    MuiButton: {
      styleOverrides: {
        root: () => ({
          textTransform: 'unset',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          }
        }),
        outlinedInherit: ({ theme }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }),
        containedInherit: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    // EXAMPLE: Defining the variant styles to go along with the EXAMPLE variant="whatever" defined in /src/components/Button/Button.tsx
    //   variants: [
    //     {
    //       props: { variant: 'whatever' },
    //       style: ({ theme }) => ({
    //         SOME STYLES HERE...
    //       }),
    //     },
    //   ],
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderWidth: '1px',
          borderStyle: 'solid',
          boxShadow: 'none',
          borderColor: theme.palette.divider,
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: theme.spacing(36),
          },
          [theme.breakpoints.up('lg')]: {
            width: theme.spacing(48),
          },
        }),
      }
    },

    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderTopColor: theme.palette.grey[200]
        })
      }
    },

    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            fontWeight: theme.typography.fontWeightMedium,
          },
          '& .MuiDataGrid-editInputCell, & .MuiDataGrid-cell': {
            fontSize: '1rem',
          },
          '& .MuiInputBase-root': {
            borderRadius: 0,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          }
        })
      }
    },

    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) => ({
          flexShrink: 0,
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: theme.spacing(theme.sizes.drawerWidth)
          },
          '@media print': {
            display: 'none',
          },
        }),
        paper: ({ theme }) => ({
          boxSizing: 'border-box',
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: theme.spacing(theme.sizes.drawerWidth)
          }
        })
      }
    },

    MuiFilledInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: '10px',
          '&:before': {
            display: 'none',
          },
        }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 'bold'
        }
      }
    },

    MuiPopover: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderWidth: '1px',
          borderSTyle: 'solid',
          borderColor: theme.palette.divider,
          borderRadius: theme.shape.borderRadius,
        }),
        paper: ({ theme }) => ({
          boxShadow: theme.shadows[8],
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: theme.palette.divider,
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
  }
}

export const theme: Theme = createTheme(options)

export default theme
